import React, { useState, useEffect, useRef } from 'react';
import { 
    FaPlay, FaPause, FaExpand, FaCompress, FaVolumeUp, FaVolumeMute, FaCog, FaClosedCaptioning 
} from 'react-icons/fa';

// Custom hook to detect clicks outside of a component
const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

const VideoControls = ({
    isPlaying,
    volume,
    isMuted,
    isFullscreen,
    areControlsVisible,
    currentTime,
    duration,
    availableSubtitles,
    activeSubtitleId,
    availableQualities,
    activeQualityId,
    onPlayPauseToggle,
    onVolumeChange,
    onToggleMute,
    onToggleFullscreen,
    onSeek,
    onSelectSubtitle,
    onSelectQuality,
    fileName
}) => {
    const [isVolumeSliderVisible, setVolumeSliderVisible] = useState(false);
    const [showSubtitleMenu, setShowSubtitleMenu] = useState(false);
    const [showQualityMenu, setShowQualityMenu] = useState(false);
    
    const subtitleMenuRef = useRef();
    const qualityMenuRef = useRef();
    
    useOnClickOutside(subtitleMenuRef, () => setShowSubtitleMenu(false));
    useOnClickOutside(qualityMenuRef, () => setShowQualityMenu(false));

    const formatTime = (timeInSeconds) => {
        const result = new Date(timeInSeconds * 1000).toISOString().substr(11, 8);
        return {
            minutes: result.substr(3, 2),
            seconds: result.substr(6, 2),
        };
    };

    const handleSeek = (e) => {
        const seekTime = (e.target.value / 100) * duration;
        onSeek(seekTime);
    };
    
    const progress = duration ? (currentTime / duration) * 100 : 0;

    return (
        <div 
            className={`absolute bottom-0 left-0 right-0 z-10 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${areControlsVisible ? 'opacity-100' : ''}`}
            onClick={(e) => e.stopPropagation()} // Prevents toggling play/pause when clicking controls
        >
            {/* Progress Bar */}
            <div className="relative w-full mb-2">
                <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={handleSeek}
                    className="w-full h-1 bg-white/20 rounded-full appearance-none cursor-pointer video-progress"
                    style={{'--progress': `${progress}%`}}
                />
            </div>

            {/* Bottom Controls */}
            <div className="flex items-center justify-between">
                {/* Left Controls */}
                <div className="flex items-center gap-4">
                    <button onClick={onPlayPauseToggle} aria-label={isPlaying ? "Pause" : "Play"} className="focus:outline-none focus:ring-2 focus:ring-primary rounded-full">
                        {isPlaying ? <FaPause size={24} /> : <FaPlay size={24} />}
                    </button>

                    <div 
                        className="flex items-center"
                        onMouseEnter={() => setVolumeSliderVisible(true)}
                        onMouseLeave={() => setVolumeSliderVisible(false)}
                    >
                        <button onClick={onToggleMute} aria-label={isMuted || volume === 0 ? "Unmute" : "Mute"}>
                            {isMuted || volume === 0 ? <FaVolumeMute size={24} /> : <FaVolumeUp size={24} />}
                        </button>
                        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isVolumeSliderVisible ? 'w-24 ml-2' : 'w-0'}`}>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={isMuted ? 0 : volume * 100}
                                onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
                                className="w-full h-1 bg-white/20 rounded-full appearance-none cursor-pointer accent-primary"
                            />
                        </div>
                    </div>

                    <div className="text-sm font-mono">
                        <span>{formatTime(currentTime).minutes}:{formatTime(currentTime).seconds}</span>
                        <span className="text-white/70"> / {formatTime(duration).minutes}:{formatTime(duration).seconds}</span>
                    </div>
                </div>

                {/* File Name (Center)  
                <div className="absolute left-1/2 -translate-x-1/2 text-sm text-white/90 font-display hidden md:block">
                    {fileName}
                </div> */}

                {/* Right Controls */}
                <div className="flex items-center gap-4 relative">
                    {/* Subtitle Selector */}
                    <div ref={subtitleMenuRef} className="relative">
                        <button 
                            onClick={() => setShowSubtitleMenu(!showSubtitleMenu)} 
                            aria-label="Subtitles"
                            className={`transition-colors ${activeSubtitleId ? 'text-primary' : ''}`}
                        >
                            <FaClosedCaptioning size={24} />
                        </button>
                        {showSubtitleMenu && availableSubtitles?.length > 0 && (
                            <div className="absolute bottom-full right-0 mb-4 bg-surface rounded-lg shadow-lg overflow-hidden w-40 animate-fade-in-up-fast">
                                <ul>
                                    <li 
                                        onClick={() => { onSelectSubtitle(null); setShowSubtitleMenu(false); }}
                                        className={`px-4 py-2 text-sm cursor-pointer hover:bg-primary/20 ${!activeSubtitleId ? 'bg-primary/30 font-semibold' : ''}`}
                                    >
                                        Off
                                    </li>
                                    {availableSubtitles.map(sub => (
                                        <li 
                                            key={sub.id} 
                                            onClick={() => { onSelectSubtitle(sub.id); setShowSubtitleMenu(false); }}
                                            className={`px-4 py-2 text-sm cursor-pointer hover:bg-primary/20 ${activeSubtitleId === sub.id ? 'bg-primary/30 font-semibold' : ''}`}
                                        >
                                            {sub.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Quality Selector */}
                    <div ref={qualityMenuRef} className="relative">
                        <button onClick={() => setShowQualityMenu(!showQualityMenu)} aria-label="Settings">
                            <FaCog size={24} />
                        </button>
                        {showQualityMenu && availableQualities?.length > 0 && (
                            <div className="absolute bottom-full right-0 mb-4 bg-surface rounded-lg shadow-lg overflow-hidden w-32 animate-fade-in-up-fast">
                                <ul>
                                    <li 
                                        onClick={() => { onSelectQuality('auto'); setShowQualityMenu(false); }}
                                        className={`px-4 py-2 text-sm cursor-pointer hover:bg-primary/20 ${activeQualityId === 'auto' ? 'bg-primary/30 font-semibold' : ''}`}
                                    >
                                        Auto
                                    </li>
                                    {availableQualities.map(quality => (
                                        <li 
                                            key={quality.id} 
                                            onClick={() => { onSelectQuality(quality.id); setShowQualityMenu(false); }}
                                            className={`px-4 py-2 text-sm cursor-pointer hover:bg-primary/20 ${activeQualityId === quality.id ? 'bg-primary/30 font-semibold' : ''}`}
                                        >
                                            {quality.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    
                    <button onClick={onToggleFullscreen} aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                        {isFullscreen ? <FaCompress size={24} /> : <FaExpand size={24} />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VideoControls; 